import type { GetMessageResp } from "@/types/messages.ts";
import React, { createContext, useContext } from "react";

interface MessageContextValue {
  isLastMessage: boolean;
  /**
   * Identity and client-reported state of the message this subtree renders.
   *
   * Here rather than passed down because a message's own text reaches the
   * screen by two different routes — its content, and the `userResponse`
   * artifact a streamed reply carries — and the components between them
   * (`ArtifactGroup`, `ArtifactItem`) have no interest in it.
   */
  message: { id: number; clientState: NonNullable<GetMessageResp["clientState"]> };
  /**
   * Emoji drawn on this message, oldest first. On one of okthx's replies these
   * are the user's own, and they are read where the reply draws its footer.
   */
  reactions?: readonly string[];
  /**
   * Whether the user may react to this message: a finished reply of okthx's
   * (`agentResponse`), including one they already reacted to, so they can
   * change it.
   */
  canReact: boolean;
  // TODO: Move more message-related values to this context (e.g., threadId, sendMessage)
  // to avoid prop drilling through intermediate components
}

const MessageContext = createContext<MessageContextValue | undefined>(undefined);

export const MessageProvider = ({
  children,
  isLastMessage,
  message,
  reactions,
  canReact,
}: {
  children: React.ReactNode;
  isLastMessage: boolean;
  message: MessageContextValue["message"];
  reactions?: readonly string[];
  canReact?: boolean;
}) => {
  const value = React.useMemo(
    () => ({ isLastMessage, message, reactions, canReact: canReact ?? false }),
    [isLastMessage, message, reactions, canReact],
  );
  return <MessageContext.Provider value={value}>{children}</MessageContext.Provider>;
};

export const useMessageContext = () => {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error("useMessageContext must be used within a MessageProvider");
  }
  return context;
};
